package co.maytes.standupbot.dashboard.pages

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.layout.MarketingShell
import co.maytes.standupbot.dashboard.components.motion.FadeInOnMount
import co.maytes.standupbot.shared.oauth.OAuthReasonCopy
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaSlack
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.navigation.Link
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Page("/signin")
@Composable
fun SignInPage() {
    val errorCode = rememberPageContext().route.params["error"]
    MarketingShell {
        Div(attrs = { classes("mk-signin") }) {
            FadeInOnMount(delayMs = 0, contentAlignment = Alignment.TopCenter) {
                SignInStack(errorCode = errorCode)
            }
        }
    }
}

@Composable
private fun SignInStack(errorCode: String?) {
    Div(attrs = { classes("mk-signin-stack") }) {
        Div(attrs = { classes("mk-signin-card") }) {
            if (errorCode != null) ErrorBanner(code = errorCode)
            Image(src = "/logo.svg", modifier = Modifier.classNames("mk-signin-logo"))
            H1(attrs = { classes("mk-signin-title") }) { Text("Sign in to StandupBot") }
            P(attrs = { classes("mk-signin-lede") }) { Text("Use your Slack workspace to get back in.") }
            SlackSignInCta()
            P(attrs = { classes("mk-signin-fine") }) {
                Text("By signing in you agree to our ")
                Link(path = "/legal/terms", text = "Terms")
                Text(" and ")
                Link(path = "/legal/privacy", text = "Privacy Policy")
                Text(".")
            }
        }
        Link(
            path = "/oauth/slack/install",
            text = "New here? Install StandupBot",
            modifier = Modifier.classNames("mk-signin-alt"),
        )
    }
}

private fun errorMessage(code: String): String = when (code) {
    "workspace-not-installed" ->
        "StandupBot has not been installed for your Slack workspace yet. " +
            "Ask an admin to install it, or install it yourself below."
    "invite-required" ->
        "Your workspace is invite only. Ask a workspace admin to invite you " +
            "(Settings, then Members) or send you an invite link."
    "invite-invalid" ->
        "This invite link is invalid or has expired. Ask the person who invited you for a new one."
    "invite-claimed" ->
        "This invite has already been used. Ask an admin to send you a new one."
    "invite-exhausted" ->
        "This invite link has reached its limit of uses. Ask an admin for a new one."
    "invite-wrong-workspace" ->
        "This invite is for a different Slack workspace. Sign in with the Slack workspace you were invited to."
    "invite-identity-mismatch" ->
        "This invite was sent to a different Slack account. Sign in as the person it was sent to."
    "account-disabled" ->
        "Your access to this workspace has been disabled. Ask a workspace admin to restore it."
    "oidc-denied" -> "You denied the Slack sign in request."
    else -> OAuthReasonCopy.signInForCode(code) ?: "Something went wrong during sign in. Try again."
}

@Composable
private fun ErrorBanner(code: String) {
    Div(
        attrs = {
            classes("mk-signin-error")
            if (code == "invite-required") classes("mk-signin-error--info")
            attr("role", "alert")
        },
    ) { Text(errorMessage(code)) }
}

@Composable
private fun SlackSignInCta() {
    Link(
        path = "/auth/slack/start",
        modifier = Modifier.classNames("mk-btn", "mk-btn--primary", "mk-btn--block"),
    ) {
        FaSlack(size = IconSize.LG)
        Span { Text("Sign in with Slack") }
    }
}
